import React, { useState, useEffect, useRef, useMemo } from 'react';

import { toast } from 'react-toastify';
import Select from 'react-select';
import Loader from '../../shared/component/Loader';
import apiServices from '../../services/service';
import { Ic } from '../../shared/component/GosIcon';
import recordsVisibilityFlow from '../../assets/img/system hirercachy.png';
import requestPermissionFlow from '../../assets/img/image.png';
import { useSelector } from 'react-redux';
import { getGeographyNodes, getOrgUnitNodes } from '../../redux/selector';

/* Manage Records Visibility — GrowthOS modal (matched to growthos/Users.jsx VisibilityModal).
   The CHROME was rebuilt to the gos modal shell + mrv2 design (View cards, Smart-Bucket badge,
   Team segment, GeographyNode/OrgUnitNode filters, access-level header strip). ALL real logic is
   unchanged: fetch existing scope via getRecordsVisibilityByUserId, fetch GeographyNode/OrgUnitNode
   reference lists, bind the current geo/org from rowData, and submit via updateRecordsVisibility +
   updateUser (exactly the legacy payloads). Dropped vs the legacy: the expand/fullscreen toggle, the
   two help flow-diagram dialogs, and the disabled Lead-Location city picker — none were in the new
   design. The GeographyNode/OrgUnitNode pickers stay `react-select` (searchable, dynamic real data)
   rather than the design's mock native <select>. */

const EXCLUSIVE_OPTIONS = [
  { value: 'own', title: 'Assigned records only', desc: 'Only records the user owns', icon: 'user' },
  { value: 'all', title: 'All eligible records', desc: 'Every record across the org', icon: 'building' },
];
const EXCLUSIVE_VALUES = EXCLUSIVE_OPTIONS.map((o) => o.value);

// Maps a GeographyNode/OrgUnitNode API record ({ id, nodeType, parentId, name, path }) to a select option
const toNodeOption = (node) => ({
  code: node.id,
  label: `${node.nodeType} - ${node.name} - ${node.path}`,
});

const gosSelect = {
  control: (base, s) => ({
    ...base,
    minHeight: 42,
    borderRadius: 10,
    fontSize: 13.5,
    borderColor: s.isFocused ? 'var(--gos-accent)' : 'var(--gos-border-strong)',
    boxShadow: s.isFocused ? '0 0 0 3px var(--gos-accent-soft)' : 'none',
    backgroundColor: 'var(--gos-surface)',
    '&:hover': { borderColor: 'var(--gos-accent)' },
  }),
  menu: (b) => ({ ...b, zIndex: 9999, backgroundColor: 'var(--gos-surface)', border: '1px solid var(--gos-border-strong)', borderRadius: 11, overflow: 'hidden' }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  option: (b, s) => ({ ...b, fontSize: 13, backgroundColor: s.isSelected ? 'var(--gos-accent-soft)' : s.isFocused ? 'var(--gos-surface-3)' : 'transparent', color: s.isSelected ? 'var(--gos-accent-ink)' : 'var(--gos-text)' }),
  placeholder: (b) => ({ ...b, color: 'var(--gos-text-3)' }),
  singleValue: (b) => ({ ...b, color: 'var(--gos-text)' }),
  input: (b) => ({ ...b, color: 'var(--gos-text)' }),
  indicatorSeparator: (b) => ({ ...b, backgroundColor: 'var(--gos-border)' }),
  dropdownIndicator: (b) => ({ ...b, color: 'var(--gos-text-3)' }),
};

const AVA_COLORS = ['#c96342', '#2e63a6', '#6a4fae', '#2c7a45', '#b34480', '#9a751c', '#4f6b8f', '#0e8c6f', '#2f7ea3', '#a35a2f'];
const hashIdx = (s) => {
  const str = String(s || '');
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % AVA_COLORS.length;
};
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || '?';

const ManageRecordsVisibility = ({ rowData, setUpdateListFlag, renderTrigger }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [helpImg, setHelpImg] = useState(null); // null | 'visibility' | 'request'

  // Exclusive scope (Own / All)
  const [recordVisibility, setRecordVisibility] = useState(['Assigned']);
  // Team & Smart Buckets scope
  const [teamScope, setTeamScope] = useState('');
  const [smartBucketsScope, setSmartBucketsScope] = useState('');
  const [loading, setLoading] = useState(false);

  // GeographyNode / OrgUnitNode — sourced from the user record (rowData) and updated via updateUser
  const [selectedLobState, setSelectedLobState] = useState(null);
  const [selectedOrgUnitNode, setSelectedOrgUnitNode] = useState(null);

  // GeographyNode / OrgUnitNode reference lists are fetched once app-wide in PageLayoutCRM — read from the store here
  const geographyNodes = useSelector(getGeographyNodes);
  const orgUnitNodes = useSelector(getOrgUnitNodes);
  const geographyOptions = useMemo(() => (geographyNodes || []).map(toNodeOption), [geographyNodes]);
  const orgUnitOptions = useMemo(() => (orgUnitNodes || []).map(toNodeOption), [orgUnitNodes]);

  // Bind the current GeographyNode selection from rowData.geoScopeNodeId once the option list loads
  useEffect(() => {
    if (!geographyOptions.length) return;
    const match = geographyOptions.find((o) => String(o.code) === String(rowData?.geoScopeNodeId));
    setSelectedLobState(match || null);
  }, [rowData?.geoScopeNodeId, geographyOptions]);

  // Bind the current OrgUnitNode selection from rowData.orgScopeNodeId once the option list loads
  useEffect(() => {
    if (!orgUnitOptions.length) return;
    const match = orgUnitOptions.find((o) => String(o.code) === String(rowData?.orgScopeNodeId));
    setSelectedOrgUnitNode(match || null);
  }, [rowData?.orgScopeNodeId, orgUnitOptions]);

  // Fetch existing permissions when modal opens
  useEffect(() => {
    if (!showPopover || !rowData?.userId) return;
    setLoading(true);
    apiServices
      .getRecordsVisibilityByUserId(rowData.userId)
      .then((response) => {
        const rv = response?.data?.recordVisibility;
        if (!rv) return;
        const general = rv.generalRecordVisibility?.toLowerCase();
        if (general) setRecordVisibility([general]);
        const team = rv.teamRecordVisibility?.toLowerCase();
        setTeamScope(team || '');
        const bucket = rv.bucketRecordVisibility?.toLowerCase();
        setSmartBucketsScope(bucket || '');
      })
      .catch((err) => console.error('Error fetching record visibility:', err))
      .finally(() => setLoading(false));
  }, [showPopover, rowData?.userId]);

  const handleOpen = () => setShowPopover(true);
  const handleClose = () => setShowPopover(false);

  const handleVisibilityToggle = (value) => {
    if (EXCLUSIVE_VALUES.includes(value)) {
      setRecordVisibility((prev) => {
        const withoutExclusive = prev.filter((v) => !EXCLUSIVE_VALUES.includes(v));
        return prev.includes(value) ? withoutExclusive : [...withoutExclusive, value];
      });
    } else {
      setRecordVisibility((prev) =>
        prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
      );
    }
  };

  const handleScopeClick = (setter, current, value) => setter(current === value ? '' : value);

  const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : null);

  const handleSubmit = () => {
    const generalValue = recordVisibility.find((v) => EXCLUSIVE_VALUES.includes(v));
    if (!generalValue) {
      toast.error('Please select a scope option.', { position: 'top-right' });
      return;
    }
    setLoading(true);
    const visibilityPayload = {
      userId: rowData?.userId,
      generalRecordVisibility: capitalize(generalValue),
      teamRecordVisibility: teamScope ? capitalize(teamScope) : null,
      bucketRecordVisibility: smartBucketsScope ? capitalize(smartBucketsScope) : null,
    };
    // updateUser only accepts this fixed set of keys — the rest come from rowData untouched
    const userUpdatePayload = {
      userId: rowData?.userId,
      email: rowData?.email,
      phoneNumber: rowData?.phoneNumber,
      empId: rowData?.empId,
      accessLevel: rowData?.accessLevel,
      orgScopeNodeId: selectedOrgUnitNode?.code ?? null,
      geoScopeNodeId: selectedLobState?.code ?? null,
    };
    Promise.all([
      apiServices.updateRecordsVisibility(visibilityPayload),
      apiServices.updateUser(userUpdatePayload),
    ])
      .then(() => {
        toast.success('Records visibility updated successfully!', { position: 'top-right' });
        setUpdateListFlag?.(true);
        handleClose();
      })
      .catch((err) => {
        console.error('Error updating record visibility:', err);
        toast.error('Failed to update records visibility.', { position: 'top-right' });
      })
      .finally(() => setLoading(false));
  };

  const userName = rowData?.display_name || `${rowData?.firstName || ''} ${rowData?.lastName || ''}`.trim() || '—';

  return (
    <>
      {/* Trigger — a caller can supply a themed one via renderTrigger(handleOpen); default is the eye icon */}
      {renderTrigger ? (
        renderTrigger(handleOpen)
      ) : (
        <div className="action-menu">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="bi bi-eye-fill"
            viewBox="0 0 16 16"
            onClick={handleOpen}
            style={{ cursor: 'pointer' }}
            title="Manage Records Visibility"
          >
            <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" />
            <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" />
          </svg>
        </div>
      )}

      {showPopover && (
        <div
          className="gos-import-overlay gos-mrv"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) handleClose();
          }}
        >
          <div className="gos-import-modal" style={{ maxWidth: 640 }} onMouseDown={(e) => e.stopPropagation()}>
            <div className="gos-import-head">
              <div className="gos-import-head-l">
                <span className="gos-mrv-ava" style={{ background: AVA_COLORS[hashIdx(userName)] }}>
                  {initials(userName)}
                </span>
                <div>
                  <div className="gos-import-title">Manage Records Visibility</div>
                  <div className="gos-import-sub">
                    The default set of records this user can see.
                    <button
                      type="button"
                      className="gos-mrv-help"
                      title="How record visibility is resolved"
                      onClick={() => setHelpImg('visibility')}
                    >
                      <Ic name="info" size={14} />
                    </button>
                    <button
                      type="button"
                      className="gos-mrv-help"
                      title="How action & field permissions apply to a request"
                      onClick={() => setHelpImg('request')}
                    >
                      <Ic name="shield" size={14} />
                    </button>
                  </div>
                </div>
              </div>
              <button type="button" className="gos-import-close" onClick={handleClose}>
                <Ic name="x" size={18} />
              </button>
            </div>

            <div className="gos-import-body">
              {loading && <Loader />}

              <div className="gos-mrv-strip">
                <div className="gos-mrv-stat">
                  <span className="gos-mrv-stat-k">User</span>
                  <span className="gos-mrv-stat-v">{userName}</span>
                </div>
                <div className="gos-mrv-stat">
                  <span className="gos-mrv-stat-k">Access level</span>
                  <span className="gos-mrv-lvl">{rowData?.accessLevel || '—'}</span>
                </div>
              </div>

              <div className="gos-mrv-label">View</div>
              <div className="gos-mrv-cards">
                {EXCLUSIVE_OPTIONS.map((opt) => {
                  const on = recordVisibility.includes(opt.value);
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      className={'gos-mrv-card' + (on ? ' on' : '')}
                      onClick={() => handleVisibilityToggle(opt.value)}
                    >
                      <span className="gos-mrv-card-ic">
                        <Ic name={opt.icon} size={18} />
                      </span>
                      <div>
                        <div className="gos-mrv-card-t">{opt.title}</div>
                        <div className="gos-mrv-card-d">{opt.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="gos-mrv-blk">
                <span className="gos-mrv-blk-ic">
                  <Ic name="list" size={16} />
                </span>
                <div className="gos-mrv-blk-tx">
                  <div className="gos-mrv-blk-t">Smart Bucket(s)</div>
                  <div className="gos-mrv-blk-d">
                    Eligible records in bucket as per bucket (L1, L2, L3) &amp; user access level (L1, L2, L3).
                  </div>
                  <div className="gos-mrv-blk-note">
                    <span className="gos-mrv-note-lbl">Note:</span> Bucket owner can see all records of their
                    bucket irrespective of the setting mentioned here.
                  </div>
                </div>
                <button
                  type="button"
                  className={'gos-mrv-badge' + (smartBucketsScope ? ' on' : '')}
                  onClick={() => handleScopeClick(setSmartBucketsScope, smartBucketsScope, 'eligible')}
                >
                  {smartBucketsScope ? 'Eligible' : 'Off'}
                </button >
              </div >

              <div className="gos-mrv-blk">
                <span className="gos-mrv-blk-ic">
                  <Ic name="leads" size={16} />
                </span>
                <div className="gos-mrv-blk-tx">
                  <div className="gos-mrv-blk-t">Team(s)</div>
                  <div className="gos-mrv-blk-d">Records visibility, where user is a team member.</div>
                  <div className="gos-mrv-blk-note">
                    <span className="gos-mrv-note-lbl">Note:</span> Team owner and Team captain can see all
                    records of their team irrespective of the setting mentioned here.
                  </div>
                  <div className="gos-mrv-blk-note">
                    Team is a ad/hoc explicit grouping of members, separate from “structural” hierarchies.
                  </div>
                </div>
                <div className="gos-mrv-seg gos-mrv-seg-blk">
                  <button
                    type="button"
                    className={'gos-mrv-seg-b' + (teamScope === 'assigned' ? ' on' : '')}
                    onClick={() => handleScopeClick(setTeamScope, teamScope, 'assigned')}
                  >
                    Assigned
                  </button>
                  <button
                    type="button"
                    className={'gos-mrv-seg-b' + (teamScope === 'all' ? ' on' : '')}
                    onClick={() => handleScopeClick(setTeamScope, teamScope, 'all')}
                  >
                    All
                  </button>
                </div>
              </div>

              <div className="gos-mrv-label" style={{ marginTop: 20 }}>
                Filters
              </div>
              <div className="gos-mrv-filters">
                <div className="gos-mrv-field">
                  <label className="gos-mrv-flabel">GeographyNode</label>
                  <Select
                    placeholder="Select GeographyNode…"
                    value={selectedLobState}
                    onChange={setSelectedLobState}
                    options={geographyOptions}
                    getOptionValue={(o) => o.code}
                    // isLoading={nodesLoading}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={gosSelect}
                  />
                </div>
                <div className="gos-mrv-field">
                  <label className="gos-mrv-flabel">OrgUnitNode</label>
                  <Select
                    placeholder="Select OrgUnitNode…"
                    value={selectedOrgUnitNode}
                    onChange={setSelectedOrgUnitNode}
                    options={orgUnitOptions}
                    getOptionValue={(o) => o.code}
                    // isLoading={nodesLoading}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={gosSelect}
                  />
                </div>
              </div>
            </div >

            <div className="gos-import-foot end">
              <div className="gos-foot-actions">
                <button type="button" className="gos-btn gos-btn-ghost" onClick={handleClose}>
                  Cancel
                </button>
                <button type="button" className="gos-btn gos-btn-primary" onClick={handleSubmit}>
                  <Ic name="check" size={16} />
                  Apply
                </button>
              </div>
            </div >
          </div >
        </div >
      )}

      {
        helpImg && (
          <div
            className="gos-import-overlay gos-mrv"
            style={{ zIndex: 1400 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setHelpImg(null);
            }}
          >
            <div className="gos-import-modal" style={{ maxWidth: 780 }} onMouseDown={(e) => e.stopPropagation()}>
              <div className="gos-import-head">
                <div className="gos-import-head-l">
                  <span className="gos-import-step-ic">
                    <Ic name={helpImg === 'visibility' ? 'info' : 'shield'} size={18} />
                  </span>
                  <div>
                    <div className="gos-import-title">
                      {helpImg === 'visibility'
                        ? 'How Record Visibility Is Resolved'
                        : 'How Action & Field Permissions Apply To A Request'}
                    </div>
                  </div>
                </div>
                <button type="button" className="gos-import-close" onClick={() => setHelpImg(null)}>
                  <Ic name="x" size={18} />
                </button>
              </div>
              <div className="gos-import-body" style={{ textAlign: 'center' }}>
                <img
                  src={helpImg === 'visibility' ? recordsVisibilityFlow : requestPermissionFlow}
                  alt={
                    helpImg === 'visibility'
                      ? 'Record visibility resolution flow'
                      : 'Action and field permission, record visibility filtering flow'
                  }
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};

export default ManageRecordsVisibility;
